import type { Me } from "@cloudserver/contracts";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import clsx from "clsx";
import {
  BellRing,
  BookOpen,
  Check,
  Globe,
  LogOut,
  Menu as MenuIcon,
  Monitor,
  Moon,
  ShieldCheck,
  Sun,
  type LucideIcon,
} from "lucide-react";
import { DropdownMenu } from "radix-ui";
import { useLayoutEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLiveUpdates } from "../api/events.js";
import { AssistantLauncher } from "../assistant/launcher.js";
import { NotificationBell } from "./notifications.js";
import { useSetLocale, useSignOut } from "../api/queries.js";
import { useProjectAccess } from "../api/users.js";
import { LANGUAGES } from "../i18n/index.js";
import { Wordmark } from "../ui/brand.js";
import { DockSlot } from "../ui/dock.js";
import { useHelp } from "../ui/help.js";
import { PHONE, useMedia } from "../ui/media.js";
import { Sheet } from "../ui/overlay.js";
import { TerminalBar } from "../terminal/bar.js";
import { useRememberPageBefore } from "../terminal/minimise.js";
import { restoreSessions } from "../terminal/restore.js";
import { Alert } from "../ui/primitives.js";
import { applyTheme, savedTheme, THEMES, type Theme } from "../ui/theme.js";
import { AlertToasts } from "./alert-inbox.js";
import { useNavProject } from "./project-switcher.js";
import { LeftNav, useRail } from "./sidebar.js";

function LiveIndicator({ state }: { state: "live" | "reconnecting" | "off" }) {
  const { t } = useTranslation();
  if (state === "off") return null;
  const label = state === "live" ? t("live.connected") : t("live.disconnected");
  return (
    <span title={label} className="flex items-center px-1">
      <span
        className={clsx(
          "size-2 rounded-full",
          state === "live" ? "bg-ok" : "bg-warn animate-pulse",
        )}
      />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** Guests may only look: said once at the top of every page of the project. */
function GuestNotice() {
  const { t } = useTranslation();
  const { projectId } = useParams({ strict: false });
  const access = useProjectAccess(projectId);
  if (access !== "guest") return null;
  return <Alert tone="neutral">{t("nav.guestNotice")}</Alert>;
}

const THEME_ICON: Record<Theme, LucideIcon> = { system: Monitor, light: Sun, dark: Moon };

function MenuRow({
  icon: Icon,
  children,
  checked,
  onSelect,
}: {
  icon: LucideIcon;
  children: ReactNode;
  checked?: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className="flex cursor-pointer select-none items-center gap-2.5 rounded-lg px-3 py-2 text-sm outline-none data-[highlighted]:bg-surface-2"
    >
      <Icon className="size-4 text-muted" aria-hidden />
      <span className="flex-1">{children}</span>
      {checked && <Check className="size-4 text-accent" aria-hidden />}
    </DropdownMenu.Item>
  );
}

/**
 * Account menu: who is signed in, theme, language, security, sign out. On
 * phones it also carries help, whose own button the top bar has no room for.
 */
function UserMenu({ me }: { me: Me }) {
  const { t, i18n } = useTranslation();
  const help = useHelp();
  const phone = useMedia(PHONE);
  const navigate = useNavigate();
  const signOut = useSignOut();
  const setLocale = useSetLocale();
  const [theme, setTheme] = useState<Theme>(savedTheme);
  const choose = (next: Theme) => {
    applyTheme(next);
    setTheme(next);
  };
  const label = "px-3 pb-1 pt-2 text-[0.7rem] font-semibold uppercase tracking-wider text-muted";
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="flex size-9 items-center justify-center rounded-full bg-accent-soft text-sm font-semibold text-accent-strong hover:ring-2 hover:ring-accent"
          aria-label={t("nav.account")}
        >
          {me.name.trim().charAt(0).toUpperCase() || "?"}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          collisionPadding={8}
          className="z-50 w-64 max-w-[calc(100vw-1rem)] rounded-xl border border-line bg-surface p-1 shadow-lg"
        >
          <div className="px-3 py-2">
            <p className="truncate text-sm font-medium">{me.name}</p>
            <p className="truncate text-xs text-muted">{me.email}</p>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <p className={label}>{t("nav.theme")}</p>
          {THEMES.map((value) => (
            <MenuRow
              key={value}
              icon={THEME_ICON[value]}
              checked={theme === value}
              onSelect={() => choose(value)}
            >
              {t(`nav.themes.${value}`)}
            </MenuRow>
          ))}
          <p className={label}>{t("common.language")}</p>
          {LANGUAGES.map((lng) => (
            <MenuRow
              key={lng}
              icon={Globe}
              checked={i18n.resolvedLanguage === lng}
              onSelect={() => {
                void i18n.changeLanguage(lng);
                setLocale.mutate(lng);
              }}
            >
              {lng === "en" ? "English" : "Русский"}
            </MenuRow>
          ))}
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          {phone && (
            <MenuRow icon={BookOpen} onSelect={() => help.open("getting-started")}>
              {t("help.title")}
            </MenuRow>
          )}
          <MenuRow icon={ShieldCheck} onSelect={() => void navigate({ to: "/security" })}>
            {t("nav.security")}
          </MenuRow>
          <MenuRow icon={BellRing} onSelect={() => void navigate({ to: "/profile/notifications" })}>
            {t("alerts.mine")}
          </MenuRow>
          <MenuRow icon={LogOut} onSelect={() => signOut.mutate(undefined)}>
            {t("common.signOut")}
          </MenuRow>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * Signed-in layout (docs/web.md, "Layout"): the top bar (menu button below
 * `lg`, logo, session tabs, live state, assistant, notifications, help,
 * account); under it the left block — project switcher and menu, or its
 * icon rail — and the centre: the page, with the right block (side panels:
 * the assistant's chat, help) inside it on the right. Below `lg` the left
 * block is a drawer from the menu button.
 *
 * The top bar fits a 320 px phone (docs/web.md, "Top bar"): the sessions
 * become one icon with a count, help moves into the account menu, and the
 * name beside the logo shows from 420 px. On every width the session tabs
 * give way first: they scroll, while the controls on the right never shrink.
 */
export function Shell({
  me,
  fullBleed = false,
  canvas = false,
  children,
}: {
  me: Me;
  /** The page takes the whole area under the top bar (server console): no left block, no margins. */
  fullBleed?: boolean;
  /**
   * The page is a canvas (dashboard): it fills the centre, exactly the
   * screen's height, without margins.
   */
  canvas?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const help = useHelp();
  const live = useLiveUpdates(!me.secondFactorRequired);
  const [drawer, setDrawer] = useState(false);
  const [rail, setRail] = useRail();
  const projectId = useNavProject();
  const ready = !me.secondFactorRequired;
  useRememberPageBefore();
  // After a reload: this browser tab's session tabs come back (once per page, before the paint).
  useLayoutEffect(() => {
    if (ready) restoreSessions(me.id);
  }, [ready, me.id]);
  return (
    // The window never scrolls: the top bar and the left block stay, the page scrolls.
    <div className="app-root flex h-dvh flex-col overflow-hidden">
      <header className="z-30 shrink-0 border-b border-line bg-surface pt-[env(safe-area-inset-top)]">
        <div className="flex h-14 items-center gap-2 px-3 sm:gap-3 sm:px-4">
          {ready && (
            <button
              type="button"
              onClick={() => setDrawer(true)}
              className={clsx(
                "rounded-lg p-2 text-muted hover:bg-surface-2 hover:text-text",
                !fullBleed && "lg:hidden",
              )}
              aria-label={t("nav.open")}
            >
              <MenuIcon className="size-5" aria-hidden />
            </button>
          )}
          <Link to="/" aria-label={t("app.name")} className="shrink-0">
            <Wordmark collapse />
          </Link>
          {/* Centre: terminal sessions as tabs (docs/terminal.md). */}
          {ready && (
            // A size container: the session bar shows its words only when they fit.
            <div className="@container flex min-w-0 flex-1 justify-center">
              <TerminalBar />
            </div>
          )}
          <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
            <LiveIndicator state={live} />
            {ready && <AssistantLauncher />}
            {ready && <NotificationBell />}
            <button
              type="button"
              onClick={() => help.open("getting-started")}
              className="hidden rounded-lg p-2 text-muted hover:bg-surface-2 hover:text-text sm:block"
              aria-label={t("help.title")}
            >
              <BookOpen className="size-5" aria-hidden />
            </button>
            <UserMenu me={me} />
          </div>
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        {ready && !fullBleed && (
          <aside
            className={clsx(
              "hidden shrink-0 flex-col border-r border-line bg-sidebar lg:flex",
              rail ? "w-14" : "w-60",
            )}
          >
            <LeftNav
              mode={rail ? "rail" : "full"}
              projectId={projectId}
              onToggleRail={() => setRail(!rail)}
            />
          </aside>
        )}
        {/* The centre: the page, and the right block inside it (docs/web.md, "Layout"). */}
        <div className="flex min-h-0 min-w-0 flex-1">
          {fullBleed ? (
            <main className="app-main flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              {children}
            </main>
          ) : canvas ? (
            <main className="app-main flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              {ready && (
                <div className="px-4 pt-3 empty:hidden">
                  <GuestNotice />
                </div>
              )}
              {children}
            </main>
          ) : (
            <main className="app-main min-w-0 flex-1 overflow-y-auto overscroll-contain px-4 py-6 pb-[max(2rem,env(safe-area-inset-bottom))] sm:px-6 lg:px-8">
              <div className="mx-auto flex max-w-6xl flex-col gap-6">
                {ready && <GuestNotice />}
                {children}
              </div>
            </main>
          )}
          {ready && <DockSlot bleed={fullBleed || canvas} />}
        </div>
      </div>
      <Sheet open={drawer} onOpenChange={setDrawer} title={t("app.name")} side="left">
        <LeftNav mode="sheet" projectId={projectId} onNavigate={() => setDrawer(false)} />
      </Sheet>
      {ready && <AlertToasts />}
    </div>
  );
}
